import { useEffect, useRef, useState, type PropsWithChildren } from "react";
import { CaretDown, Globe } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { BrandFlag } from "./BrandFlag";
import { Button } from "./Button";
import { Footer } from "./Footer";
import { selectLanguage } from "@/i18n";
import styles from "./Layout.module.css";

const LANGUAGES = [
  { code: "de", label: "Deutsch", short: "DE" },
  { code: "en", label: "English", short: "EN" },
  { code: "es", label: "Español", short: "ES" },
] as const;

type LangCode = (typeof LANGUAGES)[number]["code"];

const LANG_CODES = new Set<string>(LANGUAGES.map((l) => l.code));

function resolveLang(raw: string | undefined): LangCode {
  if (!raw) return "de";
  // Exakte Matches zuerst, dann Prefix-Match auf zweistelligen Code.
  if (LANG_CODES.has(raw)) return raw as LangCode;
  const prefix = raw.slice(0, 2);
  if (LANG_CODES.has(prefix)) return prefix as LangCode;
  return "de";
}

export function Layout({ children }: PropsWithChildren) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const current = resolveLang(i18n.language);
  const currentLabel = LANGUAGES.find((l) => l.code === current)?.short ?? "DE";

  const pick = (code: LangCode) => {
    void selectLanguage(code);
    setOpen(false);
  };

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <BrandFlag />
          <div className={styles.langWrap} ref={wrapRef}>
            <Button
              variant="ghost"
              size="compact"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label={t("nav.switchLanguage")}
              className={styles.langToggle}
            >
              <Globe size={16} weight="regular" />
              {currentLabel}
              <CaretDown size={12} weight="bold" />
            </Button>
            {open && (
              <div className={styles.langMenu} role="menu">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    role="menuitemradio"
                    aria-checked={current === lang.code}
                    className={`${styles.langOption} ${
                      current === lang.code ? styles.langOptionActive : ""
                    }`}
                    onClick={() => pick(lang.code)}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>
      <main className={styles.main}>{children}</main>
      <Footer />
    </div>
  );
}
